<template>
  <base-page-template>
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        サービス実装管理
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        @click="dialogOmakase.visible = true;"
        color="primary"
      >
        <v-icon class="mr-2">mdi-checkbox-marked-circle-auto-outline</v-icon>
        辞書からおまかせ実装
      </v-btn>
    </template>

    <owner-info
        :account="account">
    </owner-info>

    <tab-item-template>
      <template #toolbar-items>
        <el-tooltip content="新規" placement="bottom" v-if="isEditable">
          <v-btn icon @click="openCreateDialog">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </el-tooltip>
        <v-spacer></v-spacer>
        <toolbar-inline>
          <model-select
              v-model="controllerId"
              :items="controllers"
              placeholder="コントローラ"
              clearable>
          </model-select>
        </toolbar-inline>
      </template>

      <service-implementation-table
          :serviceImplementations="serviceImplementationsView"
          @openDeleteDialog="openDeleteDialog"
          @update="createServiceImplementation($event)">
      </service-implementation-table>
    </tab-item-template>

    <!-- 作成ダイアログ -->
    <simple-dialog
        :value="dialogCreate.visible"
        title-text="サービス実装作成"
        :header-text="dialogCreate.controllerId ?
          '追加・更新するサービス構成モデルを選択してください' :
          '追加・更新の対象となるコントローラを選択してください'
        "
        @close="closeCreateDialog"
        no-padding>
      <template #apply-button="{applyText, modifierKeys}">
        {{ (() => {
          const prefix = modifierKeys.alt ? '続けて' : '';
          switch(dialogCreate.updateType) {
            case 'add': return `${prefix}追加`;
            case 'update': return `${prefix}更新`;
            default: return applyText;
          }
        })() }}
      </template>
      <template #header="{headerText}">
        <h3>{{ headerText }}</h3>
        <div class="mt-1">alt キーを押しながら追加・更新ボタンを押すと、続けて操作する事ができます。</div>
      </template>
      <template #default>
        <v-container>
          <model-select
              v-model="dialogCreate.controllerId"
              :items="controllers"
              label="コントローラ"
              :rules="[v => !!v || 'コントローラが必要です']">
          </model-select>
        </v-container>
        <v-divider></v-divider>
        <service-component-model-table
            v-model="dialogCreate.serviceComponentModelId"
            :style="dialogCreate.controllerId ? {} : {
              filter: 'brightness(0.9) opacity(0.5)',
              pointerEvents: 'none'
            }"
            :selected-controller-id="dialogCreate.controllerId"
            :existing-service-implementations="serviceImplementations"
            height="500"
            @select="dialogCreate.updateType = ($event ? $event.updateType : null)">
        </service-component-model-table>
      </template>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
      v-model="dialogDelete.visible"
      title-text="サービス実装削除"
      title-color="delete"
      apply-text="削除"
      header-text="以下のサービス実装を削除します。本当によろしいですか？"
      @close="closeDeleteDialog"
      @apply="deleteServiceImplementation">
      <service-implementation-delete-form
        v-model="dialogDelete.form">
      </service-implementation-delete-form>
    </simple-dialog>

    <!-- 辞書からおまかせ実装ダイアログ -->
    <omakase-implementation-dialog
      v-model="dialogOmakase.visible"
      :initial-controller-id="controllerId"
      @done:save="loadServiceImplementations"
    />
  </base-page-template>
</template>

<script>
import {mapState, mapActions, mapGetters, mapMutations} from "vuex";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import ServiceImplementationTable from "@/components/pages/implementation/ServiceImplementationList/ServiceImplementationTable.vue";
import ServiceComponentModelTable from "@/components/pages/implementation/ServiceImplementationList/ServiceComponentModelTable.vue";
import ServiceImplementationDeleteForm from "@/components/pages/implementation/ServiceImplementationList/ServiceImplementationDeleteForm.vue";
import OwnerInfo from "@/components/organisms/OwnerInfo.vue";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import OmakaseImplementationDialog
    from "@/components/pages/implementation/ControllerShow/OmakaseImplementationDialog.vue";

export default {
  name: 'ServiceImplementationList',
  components: {
    OmakaseImplementationDialog,
    ToolbarInline,
    ModelSelect,
    TabItemTemplate,
    BasePageTemplate,
    SimpleDialog,
    ServiceImplementationTable,
    ServiceComponentModelTable,
    ServiceImplementationDeleteForm,
    OwnerInfo
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin
  ],
  data() {
    return {
      dialogCreate: {
        visible: false,
        controllerId: null,
        serviceComponentModelId: null,
        updateType: null
      },
      dialogDelete: {
        visible: false,
        form: {},
      },
      dialogOmakase: {
        visible: false,
      },
      controllerId: null
    }
  },
  created() {
    if (this.$route.params.controllerId) {
      this.controllerId = this.$route.params.controllerId
    }
  },
  computed: {
    ...mapState('hcs/serviceImplementation', ['serviceImplementations']),
    ...mapState('hcs/controller', ['controllers']),
    ...mapGetters({
      account: 'hcs/account',
    }),
    serviceImplementationsView() {
      if(this.controllerId) {
        return this.serviceImplementations.filter(si => si.controllerId === this.controllerId);
      } else {
        return this.serviceImplementations.slice();
      }
    },
  },
  methods: {
    ...mapActions({
      _createServiceImplementation: 'hcs/serviceImplementation/create',
    }),
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      await this.loadServiceImplementations();
      await this.$store.dispatch('hcs/controller/findList', {
        q: { terminalIdEq: this.account.user?.terminal?.id }
      });
    },
    /**
     * サービス実装を取得
     *
     * @return {Promise<ServiceImplementation[]>}
     */
    async loadServiceImplementations() {
      await this.$store.dispatch('hcs/serviceImplementation/findList');
    },
    openCreateDialog() {
      this.dialogCreate = {
        visible: true,
        controllerId: this.controllerId,
        serviceComponentModelId: null,
        updateType: null
      };
    },
    // 完了
    closeCreateDialog(apply, {modifierKeys, enableApply}) {
      if(apply) {
        const controllerId = this.dialogCreate.controllerId;
        const serviceComponentModelId = this.dialogCreate.serviceComponentModelId;
        const updateType = this.dialogCreate.updateType;
        this.createServiceImplementation({
          serviceComponentModelId,
          controllerId,
          updateType
        }).finally(() => {
          enableApply();
        });
        if(modifierKeys.alt) {
          return; // ダイアログを閉じない
        }
      }
      this.dialogCreate.visible = false;
    },
    openDeleteDialog(serviceImplementation) {
      this.dialogDelete = {
        visible: true,
        form: serviceImplementation,
      }
    },
    closeDeleteDialog() {
      this.dialogDelete.visible = false;
    },
    async deleteServiceImplementation() {
      try {
        await this.$store.dispatch('hcs/serviceImplementation/delete', this.dialogDelete.form.id);
        this.$store.commit({
          type: 'showSnackbar',
          message: `サービス実装が削除されました`
        });
      } catch (e) {
        console.error(e);
        this.$store.commit({
          type: 'showSnackbar',
          color: 'failed',
          message: `サービス実装の削除に失敗しました`
        });
      }
    },
    createServiceImplementation({serviceComponentModelId, controllerId, updateType}) {
      this.showSnackbar({
        message: updateType === 'add' ? 'サービス実装を作成中...' : 'サービス実装を更新中...',
        timeout: false,
        loading: true
      });

      // サービス実装作成
      return this._createServiceImplementation({controllerId, serviceComponentModelId})
          .then(() => {
            this.showSnackbar({
              message: updateType === 'add' ? 'サービス実装を作成しました' : 'サービス実装を更新しました'
            });
          }, () => {
            this.showSnackbar({
              color: 'failed',
              message: updateType === 'add' ? 'サービス実装の作成に失敗しました' : 'サービス実装の更新に失敗しました'
            });
          });
    }
  },
}
</script>
